import React, { useEffect } from 'react'

import { graphql, Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import Layout from '../../components/core/layout'
import SEO from '../../components/core/seo'
import AccreditedYouBet from '../../components/cross-template-statics/AccreditedYouBet'
import EmployerTestimonials from '../../components/cross-template-statics/EmployerTestimonials'
import ChevronBackgroundTwoTone from '../../components/elements/ChevronBackgroundTwoTone'
import ComposedFormHireUMA from '../../components/forms/ComposedFormHireUMA'
import HeroChevron from '../../components/heroes/HeroChevron'
import BigNumberStatsEmployer from '../../components/specific-to-template/employer-partners/BigNumberStatsEmployerPartners'
import Chevron from '../../components/uma-svgs/Chevron'
import { personaEnums } from '../../enums'
import usePersona from '../../hooks/usePersona'
import { above, below, colors, space } from '../../style/design-system'
import { gatsbyImage as ptGatsbyImage } from '../../types/props'

const EmployersHome = ({ className, data }) => {
  const { resetPersona, setPersona } = usePersona()
  useEffect(() => {
    setPersona(personaEnums.personas.EMPLOYER)
    return resetPersona
  }, [resetPersona, setPersona])

  return (
    <Layout>
      <SEO
        title="Hire UMA"
        description="Connect to Ultimate Medical Academy’s career-ready, allied healthcare graduates with no recruiting cost."
      />

      <div className={className}>
        <HeroChevron
          className="hero--chevron"
          backgroundImage={{
            image: data.heroImgLg.childImageSharp.gatsbyImageData,
            className: 'd-none d-sm-block',
            objectPosition: '40% top',
          }}
          backgroundImageMobile={{
            image: data.heroImgSm.childImageSharp.gatsbyImageData,
            className: 'd-sm-none',
            objectPosition: '0 10%',
          }}
          borderColor={colors.dev.yellow.e(0.9)}>
          <h1 className="h1 mt-0 white">Build your workforce with Ultimate talent.</h1>
          <p className="white lead">
            Connect to Ultimate Medical Academy’s career-ready, allied healthcare graduates with no recruiting cost.
          </p>
        </HeroChevron>
        <main>
          <div className="section--request py-6 py-lg-10">
            <div className="container">
              <div className="row d-flex">
                <div className="col-xl-6 d-none d-xl-flex">
                  <ComposedFormHireUMA makeFullWidth />
                </div>
                <div className="col-xl-5">
                  <h2 className="h1 blue">Curated talent. Strategic placement.</h2>
                  <p>
                    UMA will help you staff your healthcare organization with a solution that’s tailored to your
                    organization – all at no cost to you. Whether you’re a small regional healthcare practice or a
                    large, national healthcare organization, we have the resources to help fulfill your allied
                    healthcare staffing needs. You’ll have a dedicated account team who will assess your hiring
                    requirements and then determine the right talent pool of UMA graduates to fill it.
                  </p>
                  <p>
                    We champion our students throughout their education and as graduates, working together with our
                    valued employer partners to place them in healthcare roles.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="section--request--sm d-xl-none">
            <ComposedFormHireUMA makeFullWidth />
          </div>

          <EmployerTestimonials />

          <BigNumberStatsEmployer />

          <div className="section--skilled">
            <div className="d-md-flex align-items-center">
              <GatsbyImage image={data.skilledImg.childImageSharp.gatsbyImageData} alt="UMA Graduate" className="img" />
              <div className="content py-6 py-lg-10 px-6 px-xl-12">
                <h2>Our grads are educated and ready.</h2>
                <p>
                  Our graduates are ready to make an impact as contributing members of the allied healthcare workforce.
                  Their learning experience at UMA has prepared them for a professional, entry-level position in a
                  healthcare role. They are ready to build careers that help improve lives.
                </p>
              </div>
            </div>
          </div>

          <ChevronBackgroundTwoTone backgroundColor={colors.dev.green.d()} chevron={{ display: false }}>
            <div className="container text-white list-style-ascii-chevron">
              <div className="row">
                <div className="col-lg-4">
                  <h2>A majority of UMA programs feature course topics such as:</h2>
                </div>
                <div className="col-lg-4">
                  <ul className="text-white">
                    <li>Customer service training, office etiquette and similar soft skills</li>
                    <li>Mock interview training</li>
                    <li>Communication skills and critical thinking</li>
                    <li>Healthcare industry knowledge, including HIPAA compliance and medical terminology</li>
                  </ul>
                </div>
                <div className="col-lg-4">
                  <ul className="text-white">
                    <li>Necessary computer and technology skills</li>
                    <li>Balance of technical and people skills</li>
                    <li>
                      Select programs have externship and practicum experience, for additional real-world training
                    </li>
                  </ul>
                </div>
              </div>
              <div className="text-center mt-10">
                <Link to="/employers/our-graduates/" className="btn btn-outline-white">
                  More About UMA Grads&nbsp;&gt;
                </Link>
              </div>
            </div>
          </ChevronBackgroundTwoTone>

          <div className="section--cost py-10 py-lg-11">
            <div className="container">
              <div className="row text-center">
                <div className="col-lg-6 offset-lg-3">
                  <h2 className="mt-0">No out-of-pocket cost solution. Really.</h2>
                  <p>
                    We are committed to helping you fill your healthcare positions with no fee charged to employers.
                    Think of us as your personal hiring team, except with no cost to your organization.
                  </p>
                  <p>More about our solutions:</p>
                </div>
              </div>

              <div className="row text-center mt-6 arrows">
                <div className="col-md-4 col-lg-3 d-flex my-6 arrow-item">
                  <p className="mb-0 d-flex flex-column justify-content-end">
                    <span className="d-block font-weight-bold">Fast solutions are not a problem</span>
                    <Chevron direction="south" color={colors.dev.green.e()} className="py-5" />
                    <span className="d-block font-weight-bold">
                      We have a steady pipeline of ready allied healthcare candidates
                    </span>
                  </p>
                </div>

                <div className="col-md-4 col-lg-3 d-flex my-6 arrow-item">
                  <p className="mb-0 d-flex flex-column justify-content-end">
                    <span className="d-block font-weight-bold">Staffing can be expensive</span>
                    <Chevron direction="south" color={colors.dev.green.e()} className="py-5" />
                    <span className="d-block font-weight-bold">Our employers don&apos;t pay a fee</span>
                  </p>
                </div>

                <div className="col-md-4 col-lg-3 d-flex my-6 arrow-item">
                  <p className="mb-0 d-flex flex-column justify-content-end">
                    <span className="d-block font-weight-bold">Knowledgeable candidates are important</span>
                    <Chevron direction="south" color={colors.dev.green.e()} className="py-5" />
                    <span className="d-block font-weight-bold">Our focused curriculum is driven by industry needs</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <AccreditedYouBet />
        </main>
      </div>
    </Layout>
  )
}

EmployersHome.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    heroImgLg: ptGatsbyImage.isRequired,
    heroImgSm: ptGatsbyImage.isRequired,
    skilledImg: ptGatsbyImage.isRequired,
  }).isRequired,
}

EmployersHome.defaultProps = {
  className: undefined,
}

export const query = graphql`
  {
    heroImgSm: file(absolutePath: { regex: "/employers-home/hero-sm/" }) {
      childImageSharp {
        gatsbyImageData(width: 375, quality: 90, placeholder: NONE, layout: FIXED)
      }
    }
    heroImgLg: file(absolutePath: { regex: "/employers-home/hero-lg/" }) {
      childImageSharp {
        gatsbyImageData(width: 1371, quality: 90, placeholder: NONE, layout: FIXED)
      }
    }
    skilledImg: file(absolutePath: { regex: "/employers-home/skilled-gs/" }) {
      childImageSharp {
        gatsbyImageData(width: 870, quality: 90, placeholder: NONE, layout: FIXED)
      }
    }
  }
`

export default styled(EmployersHome)`
  .hero--chevron {
    ${below.sm`
    min-height: 650px;
  `}

    .hero__content {
      ${below.sm`
      max-width: 50%;
    `}
    }
  }

  .section--request {
    background-color: #fff;

    ${above.xl`
    background-color: transparent;
  `}
  }

  .request-info {
    background-color: ${colors.dev.gray.h()};
    border-radius: 2px;
  }

  .section--skilled {
    .img,
    .content {
      flex: 0 0 50%;
    }

    .img {
      ${below.lg`
      max-height: 400px;
    `}
    }
  }

  .arrows {
    .arrow-item {
      border-bottom: 1px solid ${colors.dev.gray.b()};
      margin: 0 auto;
      max-width: 75%;
      padding-bottom: ${space.g};

      ${above.md`
      border-bottom: 0;
      padding-bottom: 0;
    `}

      &:last-of-type {
        border-bottom: none;
        padding-bottom: 0;
      }

      p {
        line-height: 1;
        margin: 0 auto;
        padding: 0;
      }

      svg {
        margin: 0 auto;
      }
    }
  }
`
